/**
 * Launcher V2 — the client-side playbook DRAFT model, as pure ops.
 *
 * 2026-09-02 rip-out (plans/2026-09-02-001-launcher-v2-rip-out-versioning-plan.md,
 * U4 "write-through rung save") REPEALS the two decisions this module used to
 * be built around:
 *
 *  - Decision 1 ("a rung the rep added never mints a real id client-side")
 *    still holds LOCALLY — `addDraftRung` still hands out a `new:<n>`
 *    `draftKey` and no `id` — but it is no longer a standing state: every op
 *    in this module is applied and then IMMEDIATELY persisted by
 *    `playbook-draft-store.ts#persistLadder`, so an id-less rung exists only
 *    for the brief window between the click and the save response landing.
 *  - Decision 4 ("Playbook edits are staged client-side; Review's `savePlaybook`
 *    writes one version for the whole ladder") is GONE. There is no more
 *    "staged, unsaved" ladder state to reconcile: add/remove/mapping/sender
 *    each round-trip through `POST /v2/playbooks/:id/save` on their own, the
 *    moment the rep makes them. `removedRungIds` (an explicit "staged for
 *    removal" record) and `draftDivergesFromHead` (the predicate that used to
 *    gate the waterfall's drag-to-reorder affordance on "does the staged
 *    ladder still match head?") existed only to support that reconciliation
 *    and are deleted with it — nothing can diverge from head when every
 *    mutation writes through immediately.
 *
 * What a `PlaybookDraft` is NOW: the in-memory mirror of whatever the
 * playbook's head last held, kept around so `assembleDraftRungs` has
 * something to build a save payload from and so `QualifyStep`/`SequencesStep`
 * have something to render between the optimistic local apply of an op and
 * that op's save response landing.
 *
 * DOM-free and Prisma-free on purpose, the same reason `members-panel-scope.ts`
 * / `review-gate.ts` / `rung-counts.ts` / `waterfall-reorder.ts` are: `bun:test`
 * has no DOM under `mini-apps/`, so any logic that lives inside a
 * Glaze-importing `.tsx` file is untestable there. The zustand store that holds
 * one of these drafts is `playbook-draft-store.ts`; it owns nothing but the
 * `set` calls and the actual `POST` round trip, so every rule about what a
 * draft may look like is testable here.
 *
 * `draftKey` is still client-only and never sent — React needs a stable list
 * key for a rung that has no id yet, and `position`/array index churns on
 * every add, remove and reorder — a key that churns remounts the row and
 * drops focus mid-edit. `assembleDraftRungs` builds its payload field by
 * field rather than spreading, so a `draftKey` cannot ride along by accident.
 *
 * `position` is deliberately ABSENT from a `DraftRung`: the array order IS the
 * first-match-wins order, positions are derived from it (`0..n-1`) at assemble
 * time, and the endpoint renumbers them server-side anyway. Storing a position
 * per draft rung would mean a second source of truth for order that every add
 * and remove has to renumber, and a stale one is a `PlaybookRungsSchema`
 * validation failure at save time rather than a visible bug.
 */

import type { PlaybooksPlaybookVersion, PlaybookRung, SequenceMapping } from './schemas';
import { applyRungReorder } from './waterfall-reorder';

/**
 * One rung as staged in the draft: everything `PlaybookRungSchema` carries
 * except the two derived-or-server-owned fields, plus the client-only key.
 *
 * Derived from `PlaybookRung` with `Omit` rather than re-declared, so a new
 * field on the persisted rung shape cannot silently go unstaged.
 */
export type DraftRung = Omit<PlaybookRung, 'id' | 'position'> & {
  /** Absent for a rung added in this draft — the server mints it at save time. */
  id?: string;
  /** Stable React list key for this rung's row, for its whole draft lifetime. */
  draftKey: string;
};

export interface PlaybookDraft {
  /**
   * The head version this draft was last seeded from, sent as
   * `expectedVersionId` so `savePlaybookRungs` can refuse a lost update. `null`
   * means the playbook has no version yet — a first save.
   */
  baseVersionId: string | null;
  /** The ladder, in first-match-wins order, catch-all last. */
  rungs: DraftRung[];
  /**
   * A rung's OLD `draftKey`, mapped to whatever key currently addresses that
   * SAME rung — the stale-draftKey-race fix (Codex adversarial review, Bug 1,
   * 2026-09-02).
   *
   * The race: a rung staged this session carries a `new:<n>` key
   * (`addDraftRung`, above). `addPlaybookDraftRung`'s write-through save is
   * queued per-playbook (`playbook-draft-store.ts#runExclusive`); if a rep
   * acts on that SAME rung again (map it, set its sender, remove it) before
   * the add's response lands, that second action is queued BEHIND the add —
   * but its closure was built with the OLD `new:<n>` key at click time
   * (`playbook-draft-store.ts#setPlaybookDraftRungMapping` et al. close over
   * whatever `draftKey` the caller passed in). By the time the second op
   * actually runs, `adoptSavedPlaybookDraft` has already renamed the rung to
   * its real `rung:<id>` key — so a lookup for the stale `new:<n>` key finds
   * nothing, and `mapDraftRung`/`removeDraftRung` used to silently return the
   * draft UNCHANGED, dropping the rep's action with no error and no toast.
   *
   * `playbook-draft-store.ts#adoptSavedPlaybookDraft` is the one place a
   * rung's `draftKey` is ever renamed, so it is the one place that appends to
   * this map (merging forward from whatever this draft already carried, so a
   * rung renamed more than once resolves through every hop —
   * {@link resolveDraftKey} follows the whole chain). `seedDraftFromHead`
   * always starts a fresh draft with an EMPTY map: a full reseed from head
   * means everything reachable is already addressed by its current key, so
   * there is nothing left to redirect FROM.
   *
   * Lives on the draft (not store-level state) because `mapDraftRung` /
   * `removeDraftRung` are pure functions in this DOM-free module — the draft
   * itself is the only context they receive, and the redirect has to be
   * resolvable from inside them (see this bug's own writeup: "make key
   * resolution happen at EXECUTION time", which for a pure op means the data
   * has to travel with whatever `draft` the op is actually applied to).
   *
   * Bounded the same way `nextNewDraftKey`'s own scan is: one entry per
   * write-through save that renamed an id-less rung, for as long as this
   * playbook's draft stays open — not meaningfully worse than the copy-draft
   * store's own scope map, which grows on the same schedule.
   */
  keyRedirects: Record<string, string>;
}

/** The slice of a playbook head version a draft needs — `PlaybooksPlaybookVersion` satisfies it. */
export type PlaybookDraftHead = Pick<PlaybooksPlaybookVersion, 'versionId' | 'rungs'>;

/** What the rep chooses when they click "Add segmentation": which segmentation, and nothing else. */
export interface NewDraftRungInput {
  segmentationId: string;
  /**
   * `null` (the default) = unmapped. A newly added rung is unmapped until the
   * rep picks a sequence: "no sequence" is a recorded decision, and the rep
   * making it explicitly is the point of the mapping step.
   */
  mapping?: SequenceMapping | null;
}

/**
 * One assembled rung as the save endpoint receives it: `PlaybookRung` with `id`
 * OPTIONAL (absent = "mint one") and no `draftKey`.
 */
export type AssembledDraftRung = Omit<PlaybookRung, 'id'> & { id?: string };

/**
 * A rung that came from the server keys off its id, so the key is stable
 * across seed and rebase. Exported: U3C's `QualifyStep` needs to compute the
 * SAME key for a `PlaybookRung` it already has in hand (e.g. to remove a
 * persisted rung from the draft by id) without duplicating this format.
 */
export function rungDraftKey(rungId: string): string {
  return `rung:${rungId}`;
}

/**
 * The next `new:N` key, chosen as one past the highest `new:` key in use rather
 * than from a module counter or `crypto.randomUUID()`. Pure and deterministic:
 * the same draft plus the same add always produces the same key, so these ops
 * stay testable without stubbing a clock or a random source, and a draft can be
 * rebuilt from a persisted snapshot without key collisions.
 */
function nextNewDraftKey(rungs: readonly DraftRung[]): string {
  let highest = 0;
  for (const rung of rungs) {
    const match = /^new:(\d+)$/.exec(rung.draftKey);
    if (match) highest = Math.max(highest, Number(match[1]));
  }
  return `new:${highest + 1}`;
}

function draftRungFromPersisted(rung: PlaybookRung): DraftRung {
  return {
    id: rung.id,
    draftKey: rungDraftKey(rung.id),
    segmentationId: rung.segmentationId,
    mapping: rung.mapping,
    isCatchAll: rung.isCatchAll,
  };
}

/**
 * `true` when at most one rung is a catch-all and it is the last one — the
 * `PlaybookRungsSchema` invariant every op below preserves BY CONSTRUCTION
 * (a catch-all matches everybody, so anything under it is unreachable).
 *
 * Exported because it is also the honest precondition every write-through save
 * (`playbook-draft-store.ts#persistLadder`) depends on: a ladder that fails
 * this cannot be persisted, and every op in this module preserves it so a
 * save is never the first place that finds out.
 */
export function isCatchAllLast(rungs: readonly { isCatchAll: boolean }[]): boolean {
  const catchAllIndexes = rungs
    .map((rung, index) => (rung.isCatchAll ? index : -1))
    .filter((index) => index !== -1);
  if (catchAllIndexes.length === 0) return true;
  return catchAllIndexes.length === 1 && catchAllIndexes[0] === rungs.length - 1;
}

/** Where a newly added rung goes: above the catch-all if there is one, else at the end. */
function insertionIndex(rungs: readonly DraftRung[]): number {
  const catchAllIndex = rungs.findIndex((rung) => rung.isCatchAll);
  return catchAllIndex === -1 ? rungs.length : catchAllIndex;
}

/**
 * The freshly read head to adopt into the draft, or `null` to leave the draft
 * alone (review-fix U11, C6-2) — the ONE rule both of the Review step's
 * re-reads use ("Reload and review again" after a refused send, and Apply's
 * 409). Adopt only when the head really moved (`versionId` ≠ the draft's
 * base) and no rung is still mid-flight on its own write-through save (no
 * server id yet): that save's own 409 rebase re-anchors the draft, and
 * `adoptSavedPlaybookDraft`'s index-based copy-draft migration only holds for
 * a response to THIS draft's own save.
 */
export function freshHeadToAdopt(
  head: PlaybookDraftHead | null | undefined,
  draft: PlaybookDraft | null,
): PlaybookDraftHead | null {
  if (head === null || head === undefined || draft === null) return null;
  if (head.versionId === draft.baseVersionId) return null;
  return draft.rungs.every((rung) => rung.id !== undefined) ? head : null;
}

/**
 * A fresh draft off the playbook's head version.
 *
 * `head === null` (a playbook with no version yet) seeds an EMPTY ladder with a
 * `null` `baseVersionId` — not a fabricated catch-all. Inventing a rung here
 * would be a hardcoded fallback for data that has to come from a real
 * segmentation the rep picked.
 */
export function seedDraftFromHead(head: PlaybookDraftHead | null): PlaybookDraft {
  if (head === null) return { baseVersionId: null, rungs: [], keyRedirects: {} };
  return {
    baseVersionId: head.versionId,
    rungs: head.rungs.map(draftRungFromPersisted),
    keyRedirects: {},
  };
}

/** Appends a rung ABOVE the catch-all (see {@link insertionIndex}), unmapped by default. */
export function addDraftRung(draft: PlaybookDraft, input: NewDraftRungInput): PlaybookDraft {
  const added: DraftRung = {
    draftKey: nextNewDraftKey(draft.rungs),
    segmentationId: input.segmentationId,
    mapping: input.mapping ?? null,
    isCatchAll: false,
  };
  const rungs = draft.rungs.slice();
  rungs.splice(insertionIndex(rungs), 0, added);
  return { ...draft, rungs };
}

/**
 * Replaces a rung's staged mapping. `null` is the suppression case — an
 * explicit "this group gets no sequence", which is why `mapping` is
 * required-but-nullable rather than optional.
 *
 * An unknown `draftKey` returns the draft UNCHANGED rather than throwing: it
 * means the row the edit came from is already gone (a concurrent remove, or a
 * rebase that dropped a server-deleted rung), and a staged edit against a rung
 * that no longer exists has nothing to apply to.
 *
 * Round-2-fixes M2 addition — an unmap (`mapping: null`) against a rung that
 * is ALREADY unmapped returns the SAME rung reference, the same
 * reference-equality no-op contract {@link remapDraftRungSequence} and
 * {@link remapDraftRungSegmentation} already use. This is what lets
 * `playbook-draft-store.ts#persistLadderOnce`'s 409-retry short-circuit
 * recognize a retry that discovers the fresh head already unmapped the rung
 * as nothing-to-write, instead of posting a redundant save — the case
 * `draftRungMapsToSequence(playbookId, draftKey, null)` exists to
 * disambiguate on the caller side. A non-null mapping is always replaced
 * unconditionally (unlike a sequence remap, there is no "already this exact
 * mapping" short-circuit here — re-confirming the same sequence in
 * `SequenceMappingDrawer` intentionally still writes through).
 */
export function setDraftRungMapping(
  draft: PlaybookDraft,
  draftKey: string,
  mapping: SequenceMapping | null,
): PlaybookDraft {
  return mapDraftRung(draft, draftKey, (rung) =>
    rung.mapping === null && mapping === null ? rung : { ...rung, mapping },
  );
}

// Playbooks QA round 4, U9 (SN1) / Decision 3 — `setDraftRungSender` was here
// and is DELETED with its one caller (`SequencesStep.tsx`'s
// `InlineSenderPopover`). Sender is locked to the SEQUENCE identity
// (`playbooks_sequence.default_sender`) now, so a per-rung sender EDIT is a
// per-rung override, which is exactly what that decision removed. A rung still
// carries `mapping.sender` — it is just written by COPY-ON-MAP
// (`SequenceMappingDrawer`), through `setDraftRungMapping` above, rather than
// by an op of its own.

/**
 * Stages a NEW `sequenceId` onto a rung's existing mapping, leaving every
 * other mapping field untouched — this op carries ONLY the new sequence id,
 * never a whole replacement mapping.
 *
 * That is what makes it compose correctly with `playbook-draft-store.ts`'s
 * 409-retry (`persistLadderOnce`, `op(rebaseDraft(freshHead))`): a retry
 * re-applies this exact function against the FRESHLY refetched draft, so
 * `{ ...rung.mapping, sequenceId }` spreads the fresh rung's `sender`/`cc`/
 * `replyTo`/`bookingLinkIdentity` at retry time, not whatever those fields
 * were when the op was first built. The bug this fixes (plan
 * `2026-09-03-002-launcher-v2-round2-followup-codex-fixes-plan.md`, Finding
 * 3): a caller that instead built a whole replacement mapping by spreading a
 * client-side-cached snapshot captured at render time would, on retry,
 * overwrite the fresh mapping's other fields with that stale snapshot —
 * silently reverting any concurrent edit to this same rung.
 *
 * An UNMAPPED rung (`mapping === null`) is left alone: there is no mapping to
 * remap a sequence id inside of, and synthesizing one here would invent a
 * sender/channel choice only the rep can make. An unknown `draftKey` (via {@link mapDraftRung}'s
 * `keyRedirects` resolution, same as every sibling op) also no-ops.
 *
 * A mapping whose `sequenceId` is ALREADY the target is likewise left alone
 * (returns the SAME rung reference, not a copy with an identical value) —
 * Codex finding 3: this is what lets {@link mapDraftRung}'s reference-equality
 * check recognize the whole edit as a true no-op and skip both the local
 * re-render and the write-through save `playbook-draft-store.ts#persistLadder`
 * would otherwise post for a ladder that didn't actually change.
 */
export function remapDraftRungSequence(
  draft: PlaybookDraft,
  draftKey: string,
  newSequenceId: string,
): PlaybookDraft {
  return mapDraftRung(draft, draftKey, (rung) =>
    rung.mapping === null || rung.mapping.sequenceId === newSequenceId
      ? rung
      : { ...rung, mapping: { ...rung.mapping, sequenceId: newSequenceId } },
  );
}

/**
 * Playbooks QA round 4, U6 (S2) / Decision 13 — the SEGMENTATION twin of
 * {@link remapDraftRungSequence}: points ONE rung at a different segmentation
 * identity, carrying nothing else. The usage sidebar's "Save a new version"
 * for a shared segmentation forks the identity (`POST
 * /v2/segmentations/:id/duplicate` with the draft conditions) and then calls
 * this so ONLY the rung the edit was started from moves onto the fork — every
 * other place keeps the original, which is the entire point of the fork
 * branch.
 *
 * Deliberately narrow in exactly the way `remapDraftRungSequence` is: it
 * carries only `segmentationId`, so `playbook-draft-store.ts`'s 409 retry
 * (`op(rebaseDraft(freshHead))`) re-applies it against the FRESHLY refetched
 * rung and spreads that rung's current `mapping`/`isCatchAll`/`position`
 * rather than a render-time snapshot of them.
 *
 * Two differences from its sequence sibling, both from `segmentationId` being
 * a REQUIRED scalar on a rung rather than a nullable object:
 *  - there is no unmapped-rung no-op case (a rung always names a
 *    segmentation; `mapping` is what can be `null`);
 *  - remapping a rung's segmentation never touches its `mapping`, so a rung
 *    keeps whatever sequence/sender it already had.
 *
 * A rung whose `segmentationId` is ALREADY the target is left alone (SAME
 * rung reference, so {@link mapDraftRung}'s reference-equality check
 * recognizes the whole edit as a true no-op and `persistLadder` skips the
 * write) — same contract as every sibling op. An unknown `draftKey` (after
 * `keyRedirects` resolution) also no-ops.
 */
export function remapDraftRungSegmentation(
  draft: PlaybookDraft,
  draftKey: string,
  newSegmentationId: string,
): PlaybookDraft {
  return mapDraftRung(draft, draftKey, (rung) =>
    rung.segmentationId === newSegmentationId ? rung : { ...rung, segmentationId: newSegmentationId },
  );
}

/**
 * Follows `draft.keyRedirects` from `draftKey` to whatever key currently
 * addresses the same rung, hopping through the whole chain if a rung was
 * renamed more than once (Bug 1 fix — see {@link PlaybookDraft.keyRedirects}'s
 * doc comment for the race this closes).
 *
 * `seen` guards against an infinite loop if a cycle ever existed — nothing in
 * this module ever points a key back at one that redirects to it, so a real
 * cycle isn't expected, but an unbounded loop here would hang the whole
 * ladder rather than just failing one lookup the way an unresolved key
 * already does.
 *
 * Exported (round-4-adversarial-review fix, Bug 4) — `playbook-draft-store.ts`'s
 * `draftRungMapsToSequence` needs the same redirect-following lookup to read
 * a rung's CURRENT mapping straight out of the live draft: a `persistLadder`-
 * family `null` return is ambiguous between "nothing to write" and "already
 * correct, no-op" — see that function's own doc comment for the full
 * rationale.
 */
export function resolveDraftKey(draft: PlaybookDraft, draftKey: string): string {
  const seen = new Set<string>();
  let current = draftKey;
  while (draft.keyRedirects[current] !== undefined && !seen.has(current)) {
    seen.add(current);
    current = draft.keyRedirects[current];
  }
  return current;
}

/**
 * Codex finding 3 fix (round-2-followup-codex-fixes plan) — when the rung IS
 * found (`index !== -1`) but `edit` returns the SAME rung reference unchanged
 * (every sibling op's documented no-op case: `setDraftRungMapping` unmapping
 * an already-unmapped rung, `remapDraftRungSequence` on an unmapped rung,
 * `remapDraftRungSegmentation`/`remapDraftRungSequence` when
 * the mapping's `sequenceId` is already the target), this now returns the
 * ORIGINAL `draft` unchanged by reference too, instead of always building a
 * new array + object regardless of whether anything inside it actually
 * changed. This is a pre-existing, systemic gap affecting every draft op that
 * has a no-op case, not specific to any one op — a semantically-unchanged
 * edit still produced a genuinely new `PlaybookDraft` object, so nothing
 * downstream could ever tell "this op did nothing" from "this op changed
 * something" by reference, and `persistLadderOnce`
 * (`playbook-draft-store.ts`) posted a real write-through save for every
 * no-op edit.
 */
function mapDraftRung(
  draft: PlaybookDraft,
  draftKey: string,
  edit: (rung: DraftRung) => DraftRung,
): PlaybookDraft {
  const resolvedKey = resolveDraftKey(draft, draftKey);
  const index = draft.rungs.findIndex((rung) => rung.draftKey === resolvedKey);
  if (index === -1) return draft;
  const original = draft.rungs[index] as DraftRung;
  const updated = edit(original);
  if (updated === original) return draft;
  const rungs = draft.rungs.slice();
  rungs[index] = updated;
  return { ...draft, rungs };
}

/**
 * Removes a rung from the ladder. Removing a persisted rung is what makes the
 * next save DELETE it — the endpoint writes the whole `rungs` array, so a rung
 * absent from the payload is absent from the new version. As of the
 * 2026-09-02 write-through rip-out, "the next save" is immediate
 * (`playbook-draft-store.ts#persistLadder`), not a later Review-step action —
 * this function itself stays a pure, synchronous local edit either way.
 *
 * Cannot break the catch-all-last invariant: dropping one element never moves
 * another below the last one, and dropping the catch-all itself leaves a ladder
 * with none (legal per `PlaybookRungsSchema` — "must end in a catch-all" is
 * build-time playbook validation, which has to tolerate intermediate editing
 * states).
 */
export function removeDraftRung(draft: PlaybookDraft, draftKey: string): PlaybookDraft {
  const resolvedKey = resolveDraftKey(draft, draftKey);
  const removed = draft.rungs.find((rung) => rung.draftKey === resolvedKey);
  if (removed === undefined) return draft;
  const rungs = draft.rungs.filter((rung) => rung.draftKey !== resolvedKey);
  return { ...draft, rungs };
}

/**
 * Plan 2026-09-25-002, U6 (Key Technical Decision 16) — the draft in the order
 * given by `orderedRungIds` (the non-catch-all rung ids, new priority order),
 * catch-all pinned last. The play flow's reorder is a full-ladder write
 * through `persistLadder` (a play has no `/reorder` endpoint), so this is the
 * pure op that write applies.
 *
 * Validation is `waterfall-reorder.ts#applyRungReorder`'s: `orderedRungIds`
 * must be an exact permutation of the persisted non-catch-all rung ids. A
 * refused list throws (a stale or fabricated caller list, surfaced as the
 * caller's toast) rather than silently dropping or cloning a rung. An id-less
 * rung (an add still in flight) can never be named, so it also refuses; the
 * caller runs behind that add in `runExclusive`, so this does not arise in
 * practice. Returns the SAME reference when the order is unchanged, so
 * `persistLadder`'s no-op short-circuit skips the write.
 */
export function reorderDraft(draft: PlaybookDraft, orderedRungIds: readonly string[]): PlaybookDraft {
  const indexed = draft.rungs.map((rung, index) => ({
    id: rung.id ?? `unsaved:${rung.draftKey}`,
    position: index,
    isCatchAll: rung.isCatchAll,
    rung,
  }));
  const result = applyRungReorder(indexed, orderedRungIds);
  if (!result.ok) throw new Error(result.reason);
  const rungs = result.rungs.map((entry) => entry.rung);
  if (rungs.every((rung, index) => rung === draft.rungs[index])) return draft;
  return { ...draft, rungs };
}

/**
 * Re-anchors a draft onto a freshly fetched head, discarding whatever the
 * draft held before.
 *
 * Before the 2026-09-02 write-through rip-out this MERGED a draft's staged,
 * not-yet-saved edits across the seam (plan Decision 4: additions/removals
 * were staged client-side until Review's "Save Playbook"). There is nothing
 * left to merge: every rung add/remove/mapping/sender edit round-trips through
 * `POST /v2/playbooks/:id/save` the moment it happens
 * (`playbook-draft-store.ts#persistLadder`), so a draft never holds an edit
 * the server doesn't already know about by the time anything would need to
 * rebase onto a "new" head.
 *
 * Kept as a named function — rather than calling {@link seedDraftFromHead}
 * directly at its one remaining call site — purely for that call site's own
 * readability: `persistLadder`'s 409-retry path reads as "re-anchor onto the
 * head that actually exists now", which is a different intent than "seed a
 * brand-new draft" even though the implementation is now identical.
 */
export function rebaseDraft(head: PlaybookDraftHead): PlaybookDraft {
  return seedDraftFromHead(head);
}

/**
 * The ladder as `savePlaybookRungs` receives it: `draftKey` stripped, `position`
 * derived from array order, and `id` present only where the rung is already
 * persisted.
 *
 * Built field by field on purpose (no object spread of the draft rung) — that is
 * what structurally guarantees `draftKey` never reaches the wire, rather than a
 * `delete` the next field addition could forget.
 */
export function assembleDraftRungs(draft: PlaybookDraft): AssembledDraftRung[] {
  return draft.rungs.map((rung, index) => {
    const assembled: AssembledDraftRung = {
      position: index,
      segmentationId: rung.segmentationId,
      mapping: rung.mapping,
      isCatchAll: rung.isCatchAll,
    };
    if (rung.id !== undefined) assembled.id = rung.id;
    return assembled;
  });
}
